import React, { useState, useMemo } from 'react';
import { Landmark, ChevronDown, ChevronUp } from 'lucide-react';
import Sidebar from '../components/Sidebar';

const fmt = (v) =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPct = (v) =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + '%';

function calcSAC(vp, i, n) {
  const amort = vp / n;
  const rows = [];
  let saldo = vp;
  for (let m = 1; m <= n; m++) {
    const juros = saldo * i;
    const parcela = amort + juros;
    saldo -= amort;
    rows.push({ mes: m, parcela, amort, juros, saldo: Math.max(0, saldo) });
  }
  return rows;
}

function calcPrice(vp, i, n) {
  const pmt = (vp * i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
  const rows = [];
  let saldo = vp;
  for (let m = 1; m <= n; m++) {
    const juros = saldo * i;
    const amort = pmt - juros;
    saldo -= amort;
    rows.push({ mes: m, parcela: pmt, amort, juros, saldo: Math.max(0, saldo) });
  }
  return rows;
}

function calcSACRE(vp, i, n) {
  const rows = [];
  let saldo = vp;
  let prazoRestante = n;
  let pmt = (vp * i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
  for (let m = 1; m <= n; m++) {
    if (m > 1 && (m - 1) % 12 === 0 && prazoRestante > 0) {
      pmt = (saldo * i * Math.pow(1 + i, prazoRestante)) / (Math.pow(1 + i, prazoRestante) - 1);
    }
    const juros = saldo * i;
    const amort = pmt - juros;
    saldo -= amort;
    prazoRestante--;
    rows.push({ mes: m, parcela: pmt, amort, juros, saldo: Math.max(0, saldo) });
  }
  return rows;
}

function calcCET(totalPagoSemEntrada, vp, n) {
  const i = Math.pow(totalPagoSemEntrada / vp, 1 / n) - 1;
  return (Math.pow(1 + i, 12) - 1) * 100;
}

const SISTEMAS = [
  { id: 'price', label: 'Price', desc: 'Parcela fixa' },
  { id: 'sac', label: 'SAC', desc: 'Parcela decrescente' },
  { id: 'sacre', label: 'SACRE', desc: 'Recálculo anual' },
];

export default function SimuladorFinanciamento() {
  const [form, setForm] = useState({
    valorImovel: 500000,
    entrada: 20,
    entradaModo: 'pct',
    taxaAnual: 11.5,
    prazoAnos: 30,
    sistema: 'price',
    seguroMensal: 0,
    taxaAdmin: 0,
  });
  const [showTabela, setShowTabela] = useState(false);
  const [tabelaLimit, setTabelaLimit] = useState(24);

  const set = (k) => (e) => {
    const v = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const resultado = useMemo(() => {
    const vI = form.valorImovel || 0;
    const entradaR = form.entradaModo === 'pct' ? (vI * form.entrada) / 100 : form.entrada;
    const vp = vI - entradaR;
    if (vp <= 0) return null;
    const n = (form.prazoAnos || 1) * 12;
    const i = Math.pow(1 + form.taxaAnual / 100, 1 / 12) - 1;

    let rows;
    if (form.sistema === 'sac') rows = calcSAC(vp, i, n);
    else if (form.sistema === 'sacre') rows = calcSACRE(vp, i, n);
    else rows = calcPrice(vp, i, n);

    const extras = (Number(form.seguroMensal) || 0) + (Number(form.taxaAdmin) || 0);
    const totalJuros = rows.reduce((s, r) => s + r.juros, 0);
    const totalParcelas = rows.reduce((s, r) => s + r.parcela, 0);
    const totalPago = totalParcelas + extras * n + entradaR;
    const cet = calcCET(totalParcelas + extras * n, vp, n);

    return { vp, entradaR, n, rows, extras, totalJuros, totalPago, cet };
  }, [form]);

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content p-6 lg:p-8 pt-16 lg:pt-8">
        <div className="mb-8 flex items-center gap-3">
          <Landmark size={26} className="text-[#064E3B]" />
          <div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Simulador de Financiamento
            </h1>
            <p className="text-stone-500 text-sm">SAC · Price · SACRE — financiamento bancário puro</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Formulário */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
              <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Dados</h2>

              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Valor do Imóvel (R$)</label>
                <input type="number" value={form.valorImovel} onChange={set('valorImovel')}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/30" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-stone-600">Entrada</label>
                  <div className="flex rounded-md overflow-hidden border border-stone-200 text-xs">
                    {['pct', 'rs'].map((m) => (
                      <button key={m} onClick={() => setForm((p) => ({ ...p, entradaModo: m }))}
                        className={`px-2 py-1 ${form.entradaModo === m ? 'bg-[#064E3B] text-white' : 'bg-white text-stone-500 hover:bg-stone-50'}`}>
                        {m === 'pct' ? '%' : 'R$'}
                      </button>
                    ))}
                  </div>
                </div>
                <input type="number" value={form.entrada} onChange={set('entrada')}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/30" />
                {resultado && (
                  <p className="text-xs text-stone-400 mt-1">
                    Entrada: R$ {fmt(resultado.entradaR)} · Financiado: R$ {fmt(resultado.vp)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Taxa de Juros (% a.a.)</label>
                <input type="number" step="0.1" value={form.taxaAnual} onChange={set('taxaAnual')}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/30" />
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Prazo (anos)</label>
                <input type="number" min="1" max="35" value={form.prazoAnos} onChange={set('prazoAnos')}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/30" />
                <p className="text-xs text-stone-400 mt-1">{form.prazoAnos * 12} meses</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-600 mb-2">Sistema de Amortização</label>
                <div className="grid grid-cols-3 gap-2">
                  {SISTEMAS.map((s) => (
                    <button key={s.id} onClick={() => setForm((p) => ({ ...p, sistema: s.id }))}
                      className={`rounded-lg border py-2 px-1 text-center transition-colors ${
                        form.sistema === s.id ? 'border-[#064E3B] bg-[#064E3B]/5 text-[#064E3B]' : 'border-stone-200 hover:border-stone-300 text-stone-600'}`}>
                      <p className="text-sm font-bold">{s.label}</p>
                      <p className="text-[10px] text-stone-400 mt-0.5">{s.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-3">
              <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Extras (opcional)</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Seguro MIP/DFI (R$/mês)</label>
                  <input type="number" value={form.seguroMensal} onChange={set('seguroMensal')}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/30" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Taxa Admin. (R$/mês)</label>
                  <input type="number" value={form.taxaAdmin} onChange={set('taxaAdmin')}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#064E3B]/30" />
                </div>
              </div>
            </div>
          </div>

          {/* Resultados */}
          <div className="lg:col-span-3 space-y-5">
            {!resultado ? (
              <div className="bg-white border border-stone-200 rounded-xl p-8 text-center text-stone-400 text-sm">
                Preencha os dados para ver a simulação
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    {
                      label: form.sistema === 'price' ? 'Parcela Fixa' : 'Primeira Parcela',
                      value: `R$ ${fmt(resultado.rows[0].parcela + resultado.extras)}`,
                      sub: resultado.extras > 0 ? `incl. R$ ${fmt(resultado.extras)}/mês extras` : null,
                      destaque: true,
                    },
                    ...(form.sistema !== 'price' ? [{
                      label: 'Última Parcela',
                      value: `R$ ${fmt(resultado.rows[resultado.n - 1].parcela + resultado.extras)}`,
                      sub: null,
                    }] : []),
                    {
                      label: 'Total de Juros',
                      value: `R$ ${fmt(resultado.totalJuros)}`,
                      sub: `${fmtPct((resultado.totalJuros / resultado.vp) * 100)} do financiado`,
                    },
                    {
                      label: 'Total Pago',
                      value: `R$ ${fmt(resultado.totalPago)}`,
                      sub: `incl. entrada R$ ${fmt(resultado.entradaR)}`,
                    },
                    {
                      label: 'CET estimado',
                      value: fmtPct(resultado.cet),
                      sub: 'Custo Efetivo Total a.a.',
                    },
                    {
                      label: 'Prazo',
                      value: `${form.prazoAnos} anos`,
                      sub: `${resultado.n} parcelas`,
                    },
                  ].map((c) => (
                    <div key={c.label} className={`rounded-xl border p-4 ${c.destaque ? 'border-[#064E3B] bg-[#064E3B]/5' : 'border-stone-200 bg-white'}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500 mb-1">{c.label}</p>
                      <p className={`text-lg font-bold ${c.destaque ? 'text-[#064E3B]' : 'text-stone-900'}`}>{c.value}</p>
                      {c.sub && <p className="text-[10px] text-stone-400 mt-0.5">{c.sub}</p>}
                    </div>
                  ))}
                </div>

                {/* Barra de composição */}
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                  <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Composição do total pago</h3>
                  {(() => {
                    const tp = resultado.totalPago;
                    const pE = (resultado.entradaR / tp) * 100;
                    const pP = (resultado.vp / tp) * 100;
                    const pJ = (resultado.totalJuros / tp) * 100;
                    const pX = resultado.extras > 0 ? ((resultado.extras * resultado.n) / tp) * 100 : 0;
                    return (
                      <>
                        <div className="flex rounded-full overflow-hidden h-5 text-[10px] font-semibold mb-3">
                          <div style={{ width: `${pE}%` }} className="bg-stone-400 flex items-center justify-center text-white overflow-hidden">{pE > 8 ? 'Entrada' : ''}</div>
                          <div style={{ width: `${pP}%` }} className="bg-[#064E3B] flex items-center justify-center text-white overflow-hidden">{pP > 8 ? 'Principal' : ''}</div>
                          <div style={{ width: `${pJ}%` }} className="bg-amber-500 flex items-center justify-center text-white overflow-hidden">{pJ > 8 ? 'Juros' : ''}</div>
                          {pX > 0 && <div style={{ width: `${pX}%` }} className="bg-rose-400 flex items-center justify-center text-white overflow-hidden">{pX > 6 ? 'Extras' : ''}</div>}
                        </div>
                        <div className="flex flex-wrap gap-3 text-[11px] text-stone-500">
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-stone-400 inline-block" />Entrada R$ {fmt(resultado.entradaR)}</span>
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#064E3B] inline-block" />Principal R$ {fmt(resultado.vp)}</span>
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500 inline-block" />Juros R$ {fmt(resultado.totalJuros)}</span>
                          {pX > 0 && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400 inline-block" />Extras R$ {fmt(resultado.extras * resultado.n)}</span>}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Tabela de amortização */}
                <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                  <button onClick={() => setShowTabela((v) => !v)}
                    className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors">
                    <span>Tabela de Amortização ({resultado.n} parcelas)</span>
                    {showTabela ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {showTabela && (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-stone-50 border-y border-stone-100">
                            <tr>
                              {['Mês', 'Parcela', 'Amortização', 'Juros', 'Saldo Devedor'].map((h) => (
                                <th key={h} className="px-4 py-2.5 text-right first:text-left font-semibold text-stone-500 uppercase tracking-wide text-[10px]">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-50">
                            {resultado.rows.slice(0, tabelaLimit).map((r) => (
                              <tr key={r.mes} className="hover:bg-stone-50">
                                <td className="px-4 py-2 text-stone-500">{r.mes}</td>
                                <td className="px-4 py-2 text-right font-medium text-stone-900">R$ {fmt(r.parcela + resultado.extras)}</td>
                                <td className="px-4 py-2 text-right text-[#064E3B]">R$ {fmt(r.amort)}</td>
                                <td className="px-4 py-2 text-right text-amber-600">R$ {fmt(r.juros)}</td>
                                <td className="px-4 py-2 text-right text-stone-500">R$ {fmt(r.saldo)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {tabelaLimit < resultado.n && (
                        <div className="text-center py-3 border-t border-stone-100">
                          <button onClick={() => setTabelaLimit((v) => Math.min(v + 60, resultado.n))}
                            className="text-xs text-[#064E3B] hover:underline font-medium">
                            Ver mais {Math.min(60, resultado.n - tabelaLimit)} parcelas
                          </button>
                          <span className="text-stone-300 mx-2">·</span>
                          <button onClick={() => setTabelaLimit(resultado.n)} className="text-xs text-stone-400 hover:underline">Ver todas</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
